import React, { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Pencil, Plus, Trash2, UserRoundCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_FORM = { name: "", phone: "", pin: "", confirmPin: "" };
const INPUT_CLS = "rounded-none border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#1B2A6B] h-11 text-sm";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block">{label}</label>
    {children}
  </div>
);

export default function DeliveryAssociatesPage() {
  const [associates, setAssociates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    axios.get(`${API}/delivery-associates`, { withCredentials: true })
      .then(({ data }) => setAssociates(data))
      .catch(() => toast.error("Failed to load delivery associates."))
      .finally(() => setLoading(false));
  }, []);

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = associate => {
    setEditing(associate);
    setForm({ name: associate.name, phone: associate.phone, pin: "", confirmPin: "" });
    setDialogOpen(true);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required.");
    if (!form.phone.trim()) return toast.error("Phone is required.");
    if (!editing && !form.pin) return toast.error("PIN is required.");
    if (form.pin && !/^\d{4,8}$/.test(form.pin)) return toast.error("PIN must contain 4 to 8 digits.");
    if (form.pin !== form.confirmPin) return toast.error("PIN and Confirm PIN do not match.");
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), phone: form.phone.trim(), pin: form.pin };
      const request = editing
        ? axios.patch(`${API}/delivery-associates/${editing.id}`, payload, { withCredentials: true })
        : axios.post(`${API}/delivery-associates`, payload, { withCredentials: true });
      const { data } = await request;
      setAssociates(previous => {
        const next = editing
          ? previous.map(item => item.id === data.id ? data : item)
          : [...previous, data];
        return next.sort((a, b) => a.name.localeCompare(b.name));
      });
      toast.success(editing ? "Delivery associate updated." : "Delivery associate added.");
      closeDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save delivery associate.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async associate => {
    if (!window.confirm(`Delete delivery associate “${associate.name}”?`)) return;
    try {
      await axios.delete(`${API}/delivery-associates/${associate.id}`, { withCredentials: true });
      setAssociates(previous => previous.filter(item => item.id !== associate.id));
      toast.success("Delivery associate deleted.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete delivery associate.");
    }
  };

  return (
    <div className="space-y-5" data-testid="delivery-associates-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1B2A6B]">Delivery Associates</h2>
          <p className="text-xs text-[#475569] mt-0.5">{associates.length} delivery associate{associates.length !== 1 ? "s" : ""} in master data</p>
        </div>
        <Button onClick={openAdd} className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-10 px-5 border-b-2 border-[#FFD700]">
          <Plus size={14} className="mr-1.5" />Add Delivery Associate
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0]">
        {loading ? (
          <div className="flex justify-center py-20 text-sm text-[#94A3B8]">Loading...</div>
        ) : associates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <UserRoundCheck size={40} className="text-[#E2E8F0]" />
            <p className="text-sm text-[#94A3B8]">No delivery associates yet.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="bg-[#F9FAFB] border-b border-[#E2E8F0]">
              {["#", "Name", "Phone", "Added On", ""].map(item => <th key={item} className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569]">{item}</th>)}
            </tr></thead>
            <tbody>{associates.map((associate, index) => (
              <tr key={associate.id} className={`border-b border-[#E2E8F0] ${index % 2 ? "bg-[#F8FAFC]" : "bg-white"}`}>
                <td className="px-4 py-3 text-[#94A3B8] text-xs font-mono">{index + 1}</td>
                <td className="px-4 py-3 font-semibold text-[#0F172A]">{associate.name}</td>
                <td className="px-4 py-3 text-[#475569]">{associate.phone}</td>
                <td className="px-4 py-3 text-xs text-[#475569]">{format(new Date(associate.created_at), "dd MMM yyyy")}</td>
                <td className="px-4 py-3"><div className="flex gap-1">
                  <button onClick={() => openEdit(associate)} className="text-[#94A3B8] hover:text-[#1B2A6B] p-1" title="Edit"><Pencil size={14} /></button>
                  <button onClick={() => handleDelete(associate)} className="text-[#CBD5E1] hover:text-red-500 p-1" title="Delete"><Trash2 size={14} /></button>
                </div></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={open => open ? setDialogOpen(true) : closeDialog()}>
        <DialogContent className="max-w-md rounded-none p-0 gap-0">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">{editing ? "Edit Delivery Associate" : "Add Delivery Associate"}</DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">PINs are stored securely and are never displayed.</p>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-5 space-y-4">
              <Field label="Name *"><Input value={form.name} onChange={event => setForm(value => ({ ...value, name: event.target.value }))} className={INPUT_CLS} autoFocus /></Field>
              <Field label="Phone *"><Input value={form.phone} onChange={event => setForm(value => ({ ...value, phone: event.target.value }))} className={INPUT_CLS} inputMode="tel" /></Field>
              <Field label={editing ? "New PIN (leave blank to keep current)" : "PIN *"}><Input type="password" value={form.pin} onChange={event => setForm(value => ({ ...value, pin: event.target.value }))} className={INPUT_CLS} inputMode="numeric" maxLength={8} /></Field>
              <Field label={editing ? "Confirm New PIN" : "Confirm PIN *"}><Input type="password" value={form.confirmPin} onChange={event => setForm(value => ({ ...value, confirmPin: event.target.value }))} className={INPUT_CLS} inputMode="numeric" maxLength={8} /></Field>
            </div>
            <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
              <button type="button" onClick={closeDialog} className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0]">Cancel</button>
              <Button type="submit" disabled={saving} className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-60">{saving ? "Saving..." : editing ? "Update" : "Save"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
